import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Send, Image, X, Loader2, Bot, User } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CropHealthScout = ({ farm, fields, isOpen, onClose }) => {
  const { getAuthHeaders, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [chatUsage, setChatUsage] = useState({ used: 0, limit: 20, remaining: 20, month: '' });
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      loadChatUsage();
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChatUsage = async () => {
    try {
      const response = await axios.get(`${API}/crop-health-scout/usage`, { headers: getAuthHeaders() });
      setChatUsage(response.data);
    } catch (error) {
      console.error('Failed to load chat usage');
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if ((!inputMessage.trim() && !selectedImage) || isLoading) return;

    if (chatUsage.remaining <= 0) {
      toast.error(`Monthly chat limit reached (${chatUsage.limit} messages). Resets next month.`);
      return;
    }

    const messageText = inputMessage.trim();
    setInputMessage(''); // Clear input immediately
    setIsLoading(true);

    try {
      // Add user message to UI immediately
      const userMessage = {
        role: 'user',
        content: messageText,
        imageUrl: imagePreview,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, userMessage]);
      removeImage();

      // Prepare context with farm data
      const farmContext = {
        farm_name: farm?.name,
        location: farm ? `${farm.latitude}, ${farm.longitude}` : 'Unknown',
        fields: fields?.map(f => ({
          name: f.name,
          crop: f.crop,
          stage: f.stage,
          gdd: f.gdd,
          sowing_date: f.sowing_date
        })) || []
      };

      // Send to backend
      const response = await axios.post(`${API}/crop-health-scout/chat`, {
        message: messageText,
        image_url: null, // Skip image upload for now
        farm_context: farmContext
      }, { headers: getAuthHeaders() });

      // Add assistant response
      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, assistantMessage]);

      // Update usage
      if (response.data.usage) {
        setChatUsage(response.data.usage);
      } else {
        loadChatUsage();
      }

    } catch (error) {
      if (error.response?.status === 429) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to get response. Please try again.');
        console.error('Chat error:', error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render anything if not open
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 md:inset-auto md:bottom-6 md:right-6 md:w-96 md:max-w-[calc(100vw-3rem)] md:rounded-2xl shadow-2xl z-50 flex flex-col md:h-[32rem]" 
         style={{ background: 'var(--panel-strong)', border: '1px solid var(--border)' }}
         data-testid="crop-health-scout-panel">
      {/* Header */}
      <div className="text-white p-4 md:rounded-t-2xl flex items-center justify-between" style={{ background: 'linear-gradient(135deg, rgb(26,46,5), rgb(34,60,10))' }}>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.2)' }}>
            <Bot className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold">Crop Health Scout</h3>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>{chatUsage.remaining}/{chatUsage.limit} messages left this month</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-lg transition"
          style={{ background: 'transparent' }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ background: 'var(--bg1)' }}>
        {messages.length === 0 && (
          <div className="text-center py-8" style={{ color: 'var(--text-muted)' }}>
            <Bot className="w-12 h-12 mx-auto mb-3" style={{ opacity: 0.5 }} />
            <p className="text-sm">Ask me about crop health, diseases, pests, or upload a photo for diagnosis!</p>
          </div>
        )}
        
        {messages.map((msg, index) => (
          <div
            key={index}
            className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            {msg.role === 'assistant' && (
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--gold)' }}>
                <Bot className="w-4 h-4" style={{ color: 'var(--bg0)' }} />
              </div>
            )}
            <div
              className={`max-w-[80%] rounded-2xl p-3 ${
                msg.role === 'user'
                  ? 'rounded-br-md'
                  : 'rounded-bl-md'
              }`}
              style={{
                background: msg.role === 'user' ? 'var(--gold)' : 'var(--panel)',
                color: msg.role === 'user' ? 'var(--bg0)' : 'var(--text)',
                border: msg.role === 'assistant' ? '1px solid var(--border-soft)' : 'none'
              }}
            >
              {msg.imageUrl && (
                <img
                  src={msg.imageUrl}
                  alt="Uploaded"
                  className="max-w-full h-auto rounded-lg mb-2"
                />
              )}
              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
            </div>
            {msg.role === 'user' && (
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--panel)' }}>
                <User className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} />
              </div>
            )}
          </div>
        ))}
        
        {isLoading && (
          <div className="flex gap-3 justify-start">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'var(--gold)' }}>
              <Bot className="w-4 h-4" style={{ color: 'var(--bg0)' }} />
            </div>
            <div className="rounded-2xl rounded-bl-md p-3" style={{ background: 'var(--panel)', border: '1px solid var(--border-soft)' }}>
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--text-muted)' }} />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Image Preview */}
      {imagePreview && (
        <div className="px-4 py-2" style={{ background: 'var(--bg0)', borderTop: '1px solid var(--border-soft)' }}>
          <div className="relative inline-block">
            <img
              src={imagePreview}
              alt="Preview"
              className="h-16 w-auto rounded-lg"
            />
            <button
              onClick={removeImage}
              className="absolute -top-2 -right-2 text-white rounded-full p-1"
              style={{ background: 'var(--danger)' }}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSubmit} className="p-3 md:rounded-b-2xl" style={{ background: 'var(--panel)', borderTop: '1px solid var(--border-soft)' }}>
        {chatUsage.remaining <= 0 ? (
          <div className="text-center py-2 text-sm" style={{ color: 'var(--warning)' }}>
            Monthly limit reached. Resets next month.
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageSelect}
              accept="image/*"
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="p-2 rounded-lg transition"
              title="Attach image"
              style={{ color: 'var(--text-muted)', background: 'transparent' }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <Image className="w-5 h-5" />
            </button>
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Ask about crop health..."
              className="flex-1 px-3 py-2 rounded-xl text-sm focus:outline-none"
              style={{ 
                background: 'rgba(255,255,255,0.06)', 
                border: '1px solid var(--border)',
                color: 'var(--text)'
              }}
              disabled={isLoading}
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={isLoading || (!inputMessage.trim() && !selectedImage)}
              className="p-2 rounded-lg transition disabled:opacity-50"
              style={{ background: 'var(--gold)', color: 'var(--bg0)' }}
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default CropHealthScout;
